import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@mellify/ui';
import {
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Twitter,
  Plus,
  AlertCircle,
} from 'lucide-react';
import { useWorkspaceStore } from '../../../../stores/workspaceStore';
import { useQuery } from '@tanstack/react-query';
import { socialAccountService } from '../../../../services/socialAccountService';

export interface SidebarChannelListProps {
  collapsed?: boolean;
}

/**
 * Platform icon mapping
 */
const platformIcons: Record<string, React.ReactNode> = {
  facebook: <Facebook className="w-4 h-4" />,
  instagram: <Instagram className="w-4 h-4" />,
  x: <Twitter className="w-4 h-4" />,
  linkedin: <Linkedin className="w-4 h-4" />,
  youtube: <Youtube className="w-4 h-4" />,
  tiktok: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.73a8.19 8.19 0 004.76 1.52V6.79a4.83 4.83 0 01-1-.1z" />
    </svg>
  ),
  pinterest: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.477 2 2 6.477 2 12c0 4.236 2.636 7.855 6.356 9.312-.088-.791-.167-2.005.035-2.868.181-.78 1.172-4.97 1.172-4.97s-.299-.598-.299-1.482c0-1.388.806-2.425 1.808-2.425.853 0 1.265.64 1.265 1.408 0 .858-.546 2.14-.828 3.33-.236.995.5 1.807 1.48 1.807 1.778 0 3.144-1.874 3.144-4.58 0-2.393-1.72-4.068-4.177-4.068-2.845 0-4.515 2.134-4.515 4.34 0 .859.331 1.781.745 2.282a.3.3 0 01.069.288l-.278 1.133c-.044.183-.145.222-.335.134-1.249-.581-2.03-2.407-2.03-3.874 0-3.154 2.292-6.052 6.608-6.052 3.469 0 6.165 2.473 6.165 5.776 0 3.447-2.173 6.22-5.19 6.22-1.013 0-1.965-.527-2.291-1.148l-.623 2.378c-.226.869-.835 1.958-1.244 2.621.937.29 1.931.446 2.962.446 5.523 0 10-4.477 10-10S17.523 2 12 2z" />
    </svg>
  ),
  reddit: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm6.67-10a1.46 1.46 0 00-2.47-1.07 7.26 7.26 0 00-3.87-1.23l.7-3.24 2.25.49a1.04 1.04 0 102.06-.22 1.04 1.04 0 00-1.97.44l-2.52-.55a.26.26 0 00-.3.2l-.78 3.63a7.23 7.23 0 00-3.93 1.23A1.46 1.46 0 005.33 12a1.45 1.45 0 00.58 1.17 2.78 2.78 0 00-.04.48c0 2.47 2.77 4.48 6.13 4.48s6.13-2.01 6.13-4.48c0-.16-.01-.32-.04-.48.37-.27.58-.7.58-1.17zM8.68 13.04a1.04 1.04 0 011.04-1.04 1.04 1.04 0 011.04 1.04 1.04 1.04 0 01-1.04 1.04 1.04 1.04 0 01-1.04-1.04zm5.88 2.97c-.72.72-2.09.77-2.56.77s-1.84-.05-2.56-.77a.26.26 0 01.37-.37c.45.45 1.4.61 2.19.61s1.74-.16 2.19-.61a.26.26 0 01.37.37zm-.2-1.93a1.04 1.04 0 01-1.04-1.04 1.04 1.04 0 011.04-1.04 1.04 1.04 0 011.04 1.04 1.04 1.04 0 01-1.04 1.04z" />
    </svg>
  ),
  bluesky: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.785 2.643 3.593 3.519 6.173 3.175-.39.044-1.761.315-2.173.784C2.775 16.39 5.479 19.788 12 22c6.521-2.212 9.225-5.61 7.376-7.794-.412-.469-1.783-.74-2.173-.784 2.58.344 5.388-.532 6.173-3.175C23.622 9.418 24 4.458 24 3.768c0-.69-.139-1.861-.902-2.203-.659-.3-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z" />
    </svg>
  ),
};

/**
 * Platform color mapping for status indicators
 */
const platformColors: Record<string, string> = {
  facebook: 'text-blue-600',
  instagram: 'text-pink-500',
  x: 'text-dark-800 dark:text-dark-200',
  linkedin: 'text-blue-700',
  youtube: 'text-red-600',
  tiktok: 'text-dark-900 dark:text-dark-100',
  pinterest: 'text-red-700',
  reddit: 'text-orange-600',
  bluesky: 'text-blue-500',
};

/**
 * Status indicator dot colors
 */
const statusColors: Record<string, string> = {
  connected: 'bg-success-500',
  expired: 'bg-warning-500',
  error: 'bg-danger-500',
  revoked: 'bg-danger-500',
  disconnected: 'bg-dark-400',
};

/**
 * SidebarChannelList
 *
 * Displays a compact list of connected social media channels in the sidebar.
 * Shows platform icon, account name, and connection status.
 * When collapsed, shows only platform icons with status dots.
 */
export const SidebarChannelList: React.FC<SidebarChannelListProps> = ({ collapsed = false }) => {
  const currentWorkspace = useWorkspaceStore((state) => state.currentWorkspace);

  const { data: channels = [], isLoading } = useQuery({
    queryKey: ['sidebar-channels', currentWorkspace?.id],
    queryFn: () =>
      currentWorkspace?.id
        ? socialAccountService.list(currentWorkspace.id)
        : Promise.resolve([]),
    enabled: !!currentWorkspace?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return (
      <div className={cn('px-2 py-2', collapsed ? 'flex flex-col items-center gap-1' : '')}>
        {!collapsed && (
          <p className="px-3 mb-1 text-xs font-semibold text-dark-400 dark:text-dark-500 uppercase tracking-wider">
            Channels
          </p>
        )}
        {collapsed && <div className="mx-auto mb-1 w-6 h-px bg-dark-200 dark:bg-dark-700" />}
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className={cn(
              'animate-pulse rounded-lg bg-dark-100 dark:bg-dark-800',
              collapsed ? 'w-9 h-9' : 'h-8 w-full'
            )}
          />
        ))}
      </div>
    );
  }

  if (!currentWorkspace?.id) {
    return null;
  }

  return (
    <div className="py-2">
      {/* Section header */}
      {!collapsed && (
        <p className="px-3 mb-1 text-xs font-semibold text-dark-400 dark:text-dark-500 uppercase tracking-wider">
          Channels
        </p>
      )}
      {collapsed && <div className="mx-auto mb-1 w-6 h-px bg-dark-200 dark:bg-dark-700" />}

      <div className="flex flex-col gap-0.5 px-1">
        {channels.length === 0 ? (
          /* Empty state */
          <Link
            to="/dashboard/channels"
            className={cn(
              'flex items-center gap-2 rounded-lg px-2 py-2 text-dark-400 dark:text-dark-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-dark-50 dark:hover:bg-dark-800 transition-colors duration-150',
              collapsed && 'justify-center'
            )}
            title="Connect a channel"
          >
            <Plus className="w-4 h-4 shrink-0" />
            {!collapsed && (
              <span className="text-xs font-medium truncate">Connect channel</span>
            )}
          </Link>
        ) : (
          <>
            {channels.slice(0, 6).map((channel: any) => {
              const platformIcon = platformIcons[channel.platform] || (
                <AlertCircle className="w-4 h-4" />
              );
              const colorClass = platformColors[channel.platform] || 'text-dark-500';
              const statusColor = statusColors[channel.status] || 'bg-dark-400';
              const displayName = channel.accountName || channel.accountHandle || channel.platform;

              return (
                <Link
                  key={channel.id}
                  to="/dashboard/channels"
                  className={cn(
                    'group relative flex items-center gap-2 rounded-lg px-2 py-1.5 text-dark-600 dark:text-dark-300 hover:bg-dark-50 dark:hover:bg-dark-800 transition-colors duration-150',
                    collapsed && 'justify-center'
                  )}
                  title={collapsed ? `${displayName} (${channel.platform})` : undefined}
                >
                  {/* Platform icon with status dot */}
                  <div className="relative shrink-0">
                    <span className={cn(colorClass)}>{platformIcon}</span>
                    <span
                      className={cn(
                        'absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-white dark:border-dark-900',
                        statusColor
                      )}
                    />
                  </div>

                  {/* Account name */}
                  {!collapsed && (
                    <span className="text-xs font-medium truncate max-w-[140px]">
                      {displayName}
                    </span>
                  )}
                </Link>
              );
            })}

            {/* Show "more" indicator if there are more than 6 channels */}
            {channels.length > 6 && (
              <Link
                to="/dashboard/channels"
                className={cn(
                  'flex items-center gap-2 rounded-lg px-2 py-1.5 text-dark-400 dark:text-dark-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-dark-50 dark:hover:bg-dark-800 transition-colors duration-150 text-xs',
                  collapsed && 'justify-center'
                )}
                title={`View all ${channels.length} channels`}
              >
                {collapsed ? (
                  <span className="text-[10px] font-semibold">+{channels.length - 6}</span>
                ) : (
                  <span className="font-medium">
                    +{channels.length - 6} more
                  </span>
                )}
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
};

SidebarChannelList.displayName = 'SidebarChannelList';